import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...styles.page, flexDirection: isMobile ? "column" : "row" }}>
      {/* Left panel — hidden on mobile or shown as compact header */}
      <div style={{
        ...styles.left,
        display: "flex",
        padding: isMobile ? "24px" : "48px",
        minHeight: isMobile ? "auto" : "100vh",
        flex: isMobile ? "none" : 1,
      }}>
        <div style={styles.brand}>
          <div style={styles.logo}>IA</div>
          <span style={styles.brandName}>InvoiceArk</span>
        </div>
        {!isMobile && (
          <>
            <div style={styles.heroText}>
              <h1 style={styles.heroHeading}>Your invoices.<br />GRA-ready.<br />Always.</h1>
              <p style={styles.heroSub}>Built for Ghanaian businesses who take compliance seriously.</p>
            </div>
            <div style={styles.statsRow}>
              <div style={styles.stat}><span style={styles.statNum}>100%</span><span style={styles.statLabel}>GRA Compliant</span></div>
              <div style={styles.statDivider} />
              <div style={styles.stat}><span style={styles.statNum}>VAT</span><span style={styles.statLabel}>Auto-calculated</span></div>
              <div style={styles.statDivider} />
              <div style={styles.stat}><span style={styles.statNum}>GHS</span><span style={styles.statLabel}>Default Currency</span></div>
            </div>
          </>
        )}
      </div>

      {/* Right panel — form */}
      <div style={{
        ...styles.right,
        width: isMobile ? "100%" : "480px",
        flex: isMobile ? 1 : "none",
        padding: isMobile ? "24px 20px" : "48px",
        minHeight: isMobile ? "auto" : "100vh",
      }}>
        <div style={{ ...styles.card, maxWidth: isMobile ? "100%" : "360px" }}>
          <h2 style={{ ...styles.cardTitle, fontSize: isMobile ? "22px" : "28px" }}>Sign in</h2>
          <p style={styles.cardSub}>Welcome back. Enter your credentials to continue.</p>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email address</label>
              <input style={styles.input} type="email" name="email" placeholder="you@company.com" value={form.email} onChange={handleChange} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input style={styles.input} type="password" name="password" placeholder="••••••••" value={form.password} onChange={handleChange} required />
            </div>
            <button style={loading ? { ...styles.btn, ...styles.btnDisabled } : styles.btn} type="submit" disabled={loading}>
              {loading ? "Signing in..." : "Sign in →"}
            </button>
          </form>
          <p style={styles.footer}>
            Don't have an account?{" "}
            <Link to="/register" style={styles.link}>Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { display: "flex", minHeight: "100vh", fontFamily: "'Georgia', serif" },
  left: { background: "#0a0f1e", flexDirection: "column", justifyContent: "space-between", color: "#fff" },
  brand: { display: "flex", alignItems: "center", gap: "12px" },
  logo: { width: "40px", height: "40px", background: "#f6ad55", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px", color: "#0a0f1e", fontFamily: "monospace" },
  brandName: { fontSize: "18px", fontWeight: "bold", letterSpacing: "1px", color: "#fff" },
  heroText: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" },
  heroHeading: { fontSize: "48px", fontWeight: "bold", lineHeight: 1.15, marginBottom: "20px", color: "#fff" },
  heroSub: { fontSize: "16px", color: "#a0aec0", lineHeight: 1.6, maxWidth: "320px" },
  statsRow: { display: "flex", alignItems: "center", gap: "24px", paddingTop: "32px", borderTop: "1px solid #1e2a3a" },
  stat: { display: "flex", flexDirection: "column", gap: "4px" },
  statNum: { fontSize: "18px", fontWeight: "bold", color: "#f6ad55" },
  statLabel: { fontSize: "11px", color: "#718096", textTransform: "uppercase", letterSpacing: "0.5px" },
  statDivider: { width: "1px", height: "32px", background: "#1e2a3a" },
  right: { background: "#f7f8fc", display: "flex", alignItems: "center", justifyContent: "center" },
  card: { width: "100%" },
  cardTitle: { fontWeight: "bold", color: "#0a0f1e", marginBottom: "8px" },
  cardSub: { fontSize: "14px", color: "#718096", marginBottom: "36px" },
  form: { display: "flex", flexDirection: "column", gap: "20px" },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: { fontSize: "13px", fontWeight: "600", color: "#2d3748", fontFamily: "sans-serif" },
  input: { padding: "12px 14px", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", fontFamily: "sans-serif", outline: "none", background: "#fff", color: "#0a0f1e" },
  btn: { padding: "14px", background: "#0a0f1e", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "bold", cursor: "pointer", marginTop: "8px", fontFamily: "sans-serif", letterSpacing: "0.5px" },
  btnDisabled: { background: "#718096", cursor: "not-allowed" },
  footer: { textAlign: "center", fontSize: "13px", color: "#718096", marginTop: "24px", fontFamily: "sans-serif" },
  link: { color: "#f6ad55", fontWeight: "bold", textDecoration: "none" },
};

export default Login;